<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import defaultProductImage from '@/assets/imgs/defaultImg.png'
import { useCdnDomain } from '~/composables/useCdnDomain'

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  alt: {
    type: String,
    default: '',
  },
  fit: {
    type: String,
  },
})

const emit = defineEmits(['load', 'error'])

// 使用 CDN 域名替换
const { replaceCdnDomain } = useCdnDomain()
const imageRef = ref<HTMLImageElement | null>(null)
const isIntersecting = ref(false)
let observer: IntersectionObserver | null = null

// 响应式的图片源地址（应用 CDN 域名替换）
const imageSrc = computed(() => {
  return replaceCdnDomain(props.src)
})

// 响应式的占位图地址（应用 CDN 域名替换）
const placeholderSrc = computed(() => {
  if (!props.placeholder)
    return ''
  if (props.placeholder === '/assets/imgs/defaultImg.png') {
    return defaultProductImage
  }
  return replaceCdnDomain(props.placeholder)
})

const isInlineImage = computed(() => /^(?:data:|blob:)/i.test(imageSrc.value || ''))
const shouldUseImageSource = computed(() => isIntersecting.value || isInlineImage.value)
const renderedSrc = computed(() => {
  return shouldUseImageSource.value ? imageSrc.value : placeholderSrc.value
})

function observeImage() {
  if (isInlineImage.value) {
    isIntersecting.value = true
    return
  }
  if (typeof IntersectionObserver === 'undefined') {
    isIntersecting.value = true
    return
  }

  if (!observer) {
    observer = new IntersectionObserver(observerCallback, {
      rootMargin: '200px 0px',
    })
  }
  if (imageRef.value) {
    observer.observe(imageRef.value)
  }
}

function observerCallback(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      isIntersecting.value = true
      observer?.unobserve(entry.target)
    }
  })
}

function handleLoad(event: Event) {
  if (shouldUseImageSource.value)
    emit('load', event)
}

function handleError(event: Event) {
  if (shouldUseImageSource.value)
    emit('error', event)
  // imageRef.value.src = ''
}

onMounted(observeImage)

watch(imageSrc, async () => {
  // PageRender can replace the protected source after client-side decryption.
  // Reset and observe again so an image that was previously outside the
  // viewport is not left permanently on its placeholder.
  isIntersecting.value = isInlineImage.value
  await nextTick()
  observeImage()
})

onUnmounted(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <div class="lazy-image">
    <img
      ref="imageRef"
      :style="{
        opacity: renderedSrc ? 1 : 0,
      }"
      :src="renderedSrc"
      :alt="alt"
      :fit="fit"
      referrerpolicy="no-referrer"
      class="lazy-image__img"
      @load="handleLoad"
      @error="handleError"
    >
    <div v-if="!shouldUseImageSource" class="lazy-image__placeholder">
      <slot name="placeholder">
        <div class="placeholder-content" />
      </slot>
    </div>
  </div>
</template>

<style scoped>
.lazy-image {
  position: relative;
}
.lazy-image__img {
  display: block;
  width: 100%;
  height: 100%;
}
.lazy-image__placeholder {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.placeholder-content {
  color: #999;
  font-size: 14px;
}
</style>
